import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Box,
    IconButton,
    Typography, Tooltip, Alert, CircularProgress
} from "@mui/material";
import {Add, Delete} from '@mui/icons-material';
import CategoryService from "../../../logic/ProductAndCategoryService";
import ProductAndCategoryService from "../../../logic/ProductAndCategoryService";
import {ProductRequestDTO} from "../../../types/products/interfaces";

interface AddGoodsDialogProps {
    open: boolean;
    onClose: () => void;
    onAddProduct: (product: ProductRequestDTO) => void;
}

interface SizeEntry {
    size: string;
    count: number;
}

const AddProductDialog: React.FC<AddGoodsDialogProps> = ({ open, onClose, onAddProduct }) => {
    const [id, setId] = useState('');
    const [name, setName] = useState('');
    const [price, setPrice] = useState<number | string>('');
    const [description, setDescription] = useState('');
    const [sizes, setSizes] = useState<SizeEntry[]>([]);
    const [categories, setCategories] = useState<{ [key: string]: string[] }>({});
    const [selectedCategories, setSelectedCategories] = useState<{ [key: string]: string }>({});
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryValue, setNewCategoryValue] = useState<{ [key: string]: string }>({});
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchCategories = async () => {
        try {
            const fetchedCategories = await CategoryService.getAllCategories();
            const categoryMap: { [key: string]: string[] } = {};
            fetchedCategories.forEach(cat => {
                categoryMap[cat.name] = cat.values || [];
            });
            setCategories(categoryMap);
            setSelectedCategories(previous => {
                const next: { [key: string]: string } = {};
                fetchedCategories.forEach(category => {
                    next[category.name] = previous[category.name]
                        || category.values.find(value => value === 'Не указано')
                        || category.values[0]
                        || 'Не указано';
                });
                return next;
            });
        } catch {
            setError('Не удалось загрузить категории. Проверьте соединение с backend.');
        }
    };


    useEffect(() => {
        if (open) {
            setError('');
            fetchCategories();
        }
    }, [open]);

    const handleAddSize = () => {
        setSizes([...sizes, { size: '', count: 1 }]);
    };

    const handleRemoveSize = (index: number) => {
        setSizes(sizes.filter((_, i) => i !== index));
    };

    const handleDeleteValue = async (category: string, value: string) => {
        if (value === 'Не указано') return;
        const success = await ProductAndCategoryService.deleteParamInCategory(category, value);
        if (success) {
            await fetchCategories();
        } else {
            setError('Не удалось удалить значение категории.');
        }
    };

    const handleSizeChange = (index: number, field: keyof SizeEntry, value: string | number) => {
        const newSizes = [...sizes];
        newSizes[index] = { ...newSizes[index], [field]: value };
        setSizes(newSizes);
    };

    const handleCategoryChange = (category: string, value: string) => {
        setSelectedCategories(prev => ({
            ...prev,
            [category]: value
        }));
    };

    const handleAddNewCategory = async () => {
        if (newCategoryName) {
            const success = await CategoryService.createCategory(newCategoryName.trim());
            if (success) await fetchCategories();
            else setError('Не удалось создать категорию.');
            setNewCategoryName('');
        }
    };

    const handleAddNewValue = async (category: string) => {
        const value = newCategoryValue[category];
        if (value) {
            const success = await CategoryService.addParamInCategory(category, value.trim());
            if (success) {
                await fetchCategories();
                handleCategoryChange(category, value.trim());
            }
            else setError('Не удалось добавить значение категории.');
            setNewCategoryValue(prev => ({
                ...prev,
                [category]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        setError('');
        if (!id.trim() || !name.trim() || !description.trim()) {
            setError('Заполните ID, название и описание товара.');
            return;
        }
        if (Number(price) <= 0) {
            setError('Цена должна быть больше нуля.');
            return;
        }
        if (sizes.length === 0 || sizes.some(size => !size.size.trim() || size.count < 1)) {
            setError('Добавьте хотя бы один размер и укажите количество.');
            return;
        }
        const newProduct: ProductRequestDTO = {
            id: id.trim(),
            name: name.trim(),
            price: Number(price),
            description: description.trim(),
            categories: Object.keys(categories).map(category => ({
                name: category,
                value: selectedCategories[category] || 'Не указано'
            })),
            positions: sizes.map(size => ({
                size: size.size,
                count: size.count
            }))
        };

        setSaving(true);
        try {
            const success = await ProductAndCategoryService.createProduct(newProduct);
            if (!success) {
                setError('Не удалось создать товар. Проверьте уникальность ID и заполнение полей.');
                return;
            }
            onAddProduct(newProduct);
            setId('');
            setName('');
            setPrice('');
            setDescription('');
            setSizes([]);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{sx: {borderRadius: 3}}}>
            <DialogTitle>Добавить новый товар</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                <TextField
                    autoFocus
                    margin="dense"
                    label="ID / артикул"
                    helperText="Уникальный идентификатор, например jacket-winter-104"
                    fullWidth
                    value={id}
                    onChange={(e) => setId(e.target.value.replace(/\s+/g, '-').toLowerCase())}
                />
                <TextField
                    margin="dense"
                    label="Название"
                    type="text"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <TextField
                    margin="dense"
                    label="Цена"
                    type="number"
                    fullWidth
                    value={price}
                    onChange={(e) => setPrice(Math.max(0, Number(e.target.value)))}
                    error={Number(price) <= 0}
                    helperText={Number(price) <= 0 ? 'Цена должна быть больше нуля' : ''}
                />
                <TextField
                    margin="dense"
                    label="Описание"
                    type="text"
                    fullWidth
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                <Typography variant="h6" marginTop={2}>Категории и значения</Typography>
                {Object.keys(categories).map((category, idx) => (
                    <Box key={idx} marginBottom={2}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>{category}</InputLabel>
                            <Select
                                value={selectedCategories[category] || ''}
                                onChange={(e) => handleCategoryChange(category, e.target.value as string)}
                                renderValue={(selected) => <span>{selected}</span>} // Отображаем только текст выбранного значения
                            >
                                {categories[category].map((value, index) => (
                                    <MenuItem key={index} value={value} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{value}</span>
                                        <Tooltip title="Удалить параметр">
                                            <IconButton
                                                size="small"
                                                disabled={value === 'Не указано'}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDeleteValue(category, value);
                                                }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box display="flex" alignItems="center" marginTop={1}>
                            <TextField
                                margin="dense"
                                label={`Добавить новое значение в ${category}`}
                                type="text"
                                value={newCategoryValue[category] || ''}
                                onChange={(e) => setNewCategoryValue(prev => ({
                                    ...prev,
                                    [category]: e.target.value
                                }))}
                            />
                            <IconButton onClick={() => handleAddNewValue(category)}>
                                <Add />
                            </IconButton>
                        </Box>
                    </Box>
                ))}

                <Typography>Для добавления новой категории напишите её название в поле ниже</Typography>
                <Box display="flex" alignItems="center" marginBottom={2}>
                    <TextField
                        margin="dense"
                        label="Добавить новую категорию"
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                    <IconButton onClick={handleAddNewCategory}>
                        <Add />
                    </IconButton>
                </Box>

                <Box marginTop={2}>
                    <Typography variant="h6">Размеры и количество</Typography>
                    {sizes.map((size, index) => (
                        <Box key={index} display="flex" alignItems="center" marginBottom={1}>
                            <TextField
                                margin="dense"
                                label="Размер"
                                type="text"
                                value={size.size}
                                onChange={(e) => handleSizeChange(index, 'size', e.target.value)}
                                style={{ marginRight: 8 }}
                            />
                            <TextField
                                margin="dense"
                                label="Количество"
                                type="number"
                                value={size.count}
                                onChange={(e) => handleSizeChange(index, 'count', Math.max(1, Number(e.target.value)))}
                                style={{ marginRight: 8 }}
                                error={Number(size.count) < 1}
                                helperText={Number(size.count) < 1 ? 'Количество должно быть не менее 1' : ''}
                            />
                            <Tooltip title="Удалить размер">
                                <IconButton onClick={() => handleRemoveSize(index)}>
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                    <Button variant="outlined" onClick={handleAddSize} startIcon={<Add />}>
                        Добавить размер
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={saving}>
                    Отмена
                </Button>
                <Button onClick={handleSubmit} color="secondary" variant="contained" disabled={saving}>
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Добавить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddProductDialog;
